import changes from './cleanup-changes-en'

/**
 * Planning Phase 5 (plan Task 10, design §11): the consultant's AI drawing clean-up panel on the case page. Layer
 * names, label text, reasons and notes come from the drawing or the model and are passed in as plain text; no layer
 * name, no credit and no price appears here (PO 22). `provider` and `processedIn` are read by Task 11's Settings and
 * change list too; `changes` is the officer's change list (Task 11). ms and zh use the same placeholders.
 */
export default {
  title: 'Clean up my drawing with AI',
  intro:
    "If your drawing fails the drawing check, gisify can help you fix it before you submit. The original is kept, nothing is submitted, and the council's AI reads a summary of the drawing, never the file.",
  link: 'Get help fixing this drawing',
  round: 'Round {{n}} of {{max}}',
  explanation: "The AI's note",
  errorRow: '{{type}} on layer {{layer}}',
  errorRowsMore: 'and {{count}} more',
  start: {
    file: 'Drawing (DWG or DXF, up to {{mb}} MB)',
    crs: 'Coordinate system of the drawing',
    button: 'Upload and check',
    uploading: 'Uploading…',
    processedBy:
      "The original is kept, nothing is submitted, and the council's AI ({{provider}}, {{where}}) reads a summary of the drawing, never the file.",
  },
  provider: { anthropic: 'Anthropic API', bedrock: 'AWS Bedrock', openai: 'OpenAI', ollama: 'Ollama' },
  processedIn: {
    anthropic_api: 'processed by Anthropic, outside AWS',
    singapore_only: 'processed in Singapore only',
    united_states_only: 'processed in the United States only',
    any_aws_region: 'processed in whichever AWS region AWS chooses',
    openai_api: 'processed by OpenAI',
    own_server: 'processed on your Ollama server',
  },
  step: {
    queued: 'Waiting to start…',
    reading: 'Reading the drawing…',
    checking: 'Running the drawing check…',
    asking_ai: 'Asking the AI for suggestions…',
    applying: 'Applying your changes…',
    writing: 'Saving the new drawing…',
  },
  failures: {
    before: 'Problems at the start',
    now: 'Problems now',
    none: 'The drawing passes the check.',
    onLayer: 'on {{layer}}',
    count: '{{count}} found',
  },
  status: {
    uploading: 'The drawing did not finish uploading. Stop this clean-up and start again.',
    stopped: 'This clean-up was stopped.',
    failed: 'This clean-up could not go on.',
    submitted: 'The cleaned drawing is now the drawing of this case. Submit it as usual.',
    expired: 'This clean-up closed after 7 days without activity.',
    superseded: 'This clean-up was for an earlier revision of the case and is closed.',
  },
  batch: {
    title: 'Fixes gisify found',
    accept: 'Accept these',
    saveR2000: 'Save as AutoCAD 2000: leave out paper space and {{count}} other items',
    saveR2000Detail:
      'Left out: {{paperSpace}} paper-space items, {{objects}} objects, {{displayCodes}} display settings, {{xdata}} extended data, {{reactors}} links and {{layers}} unused layers.',
    held: '{{count}} more fixes will be offered in the next round.',
    textToMtextUntick:
      'Without this, the new file cannot hold these labels: the round will stop until you tick it or fix them in CAD.',
  },
  op: {
    rename_layer: 'Rename layer {{from}} to {{to}}',
    explode_inserts: 'Break {{count}} blocks on {{layer}} into their shapes',
    scale_units: 'Convert the drawing from {{unit}} to metres',
    close_outline: 'Close the outline on {{layer}} (the gap is under 1 mm)',
    join_pieces: 'Join the lines on {{layer}} that meet end to end into single outlines ({{count}} in all)',
    show_layer: 'Turn on layer {{layer}}',
    remove_empty_layer: 'Remove empty layer {{layer}}',
    remove_degenerate: 'Remove {{count}} zero-length shapes on {{layer}}',
    remove_duplicate: 'Remove {{count}} duplicate shapes on {{layer}}',
    text_to_mtext:
      'Save {{count}} labels on {{layer}} as multi-line text, so AutoCAD 2000 can keep their characters (the words do not change)',
  },
  unit: { m: 'metres', mm: 'millimetres', cm: 'centimetres', ft: 'feet' },
  question: {
    title: 'Question {{n}} of {{total}}',
    guess: 'Suggested answer',
    reason: 'Why',
    byRule: 'Suggested by gisify',
    byAi: 'Suggested by the AI',
    accept: 'Accept',
    change: 'Choose another answer',
    skip: 'Skip',
    choose: 'Your answer',
    previous: 'Previous question',
    next: 'Next question',
    answeredAccept: 'You accepted this.',
    answeredReject: 'You skipped this.',
    allAnswered: 'Every question has an answer. Press Apply when you are ready.',
    layers: 'Layers: {{layers}}',
  },
  ask: {
    choose_site_boundary: 'Which shape is the site boundary?',
    assign_layer: 'Which standard layer should {{layer}} be?',
    assign_shapes: 'Which land-use layer do these {{count}} shapes belong on?',
    set_units: 'What unit was this drawing drawn in?',
    attach_label: 'Which plot does the label {{text}} belong to?',
    label_plot: 'Which land use is this plot?',
    remove_layers: 'Remove these layers ({{group}})?',
    remove_entities: 'Remove the {{type}} items on {{layer}}? gisify cannot save them in the new file.',
  },
  answer: { shape: 'Shape {{id}}', remove: 'Remove them' },
  group: { title_block: 'title block', dimensions: 'dimensions', furniture: 'furniture', other: 'other' },
  ruleReason: {
    writer_cannot_carry:
      'An AutoCAD 2000 file cannot hold these items. Remove them here, or fix them in CAD and upload again.',
  },
  pointOut: {
    title: 'Fix these in CAD',
    help: 'gisify does not move points or areas. Fix these in your CAD program, then upload again.',
    fixInCad: 'Fix this in CAD',
    outsideJurisdiction:
      "The drawing is not where the council's area is. Check the coordinate system you chose, or place the drawing with the placement tool.",
    kind: {
      block_too_large: 'This block is too large to break up here. Explode it in CAD.',
      arc_chain: 'These pieces include arcs, which gisify does not join. Join them in CAD.',
      too_large: 'This change is too large for the clean-up to make in one round. Make it in CAD.',
    },
  },
  view: {
    title: 'Your drawing',
    loading: 'Loading the drawing…',
    failed: 'The drawing could not be shown.',
    cut: 'Part of the drawing is not shown here: it has more points than this view draws.',
    role: {
      site_boundary: 'Site boundary',
      landuse: 'Land use',
      labels: 'Land-use labels',
      context: 'Supporting detail',
      other: 'Other layers',
    },
  },
  actions: {
    apply: 'Apply',
    use: 'Use this drawing',
    useNote:
      'The cleaned drawing becomes your drawing for this case. You still submit as usual, and the normal check runs.',
    download: 'Download the cleaned DXF',
    revert: 'Go back to round {{n}}',
    stop: 'Stop',
    retry: 'Try again',
  },
  ai: {
    failed: 'The AI could not suggest fixes. Try again.',
    limit: 'Contact your council to continue.',
    roundFull: 'This round has no room left for suggestions from the AI. The AI will be asked in the next round.',
  },
  limit: {
    rounds: 'You have used all 10 rounds. Fix the remaining problems in CAD, or download the partly cleaned DXF.',
  },
  refusal: {
    not_home_tenant: 'This clean-up can be used only by people of this council.',
    not_found: 'This case could not be found.',
    actor_not_permitted: 'Only the consultant who owns this case can clean up its drawing.',
    drawing_cleanup_off: 'The council has turned the drawing clean-up off.',
    ai_not_configured: 'The council has not set up its AI for the drawing clean-up yet.',
    ai_region_unavailable: "The council's AI choice is no longer offered. Ask the council to choose it again.",
    register_server_not_configured: 'The council has not finished setting up its drawing check.',
    stage_held_externally: 'Another system holds this case, so its drawing cannot change here.',
    stage_locked: 'The drawing of this case cannot change at this stage.',
    body_invalid: 'That request could not be read. Reload the page and try again.',
    upload_rejected: 'That file cannot be cleaned up.',
    cleanup_not_found: 'This clean-up could not be found. Reload the page.',
    cleanup_in_progress: 'A clean-up is already open for this case.',
    cleanup_closed: 'This clean-up is closed.',
    upload_missing: 'The drawing did not arrive in full. Start again.',
    cleanup_busy: 'A round is still running. Wait for it to finish.',
    round_mismatch: 'This clean-up has moved on. Reload the page to see the current round.',
    answer_invalid: 'That answer cannot be used.',
    revert_invalid: 'That round can no longer be brought back.',
    retry_not_offered: 'There is nothing to try again.',
    round_limit: 'All 10 rounds are used.',
    nothing_to_use: 'No change has been applied yet.',
    cleanup_use_failed: 'The cleaned drawing could not be put on the case. Try again.',
    edit_invalid: 'A change no longer fits the drawing, so the round was undone. Check your answers and apply again.',
    unsupported_entities:
      'The new file cannot hold some items. Remove them with the questions here, tick the fix that keeps them, or fix them in CAD.',
    too_complex:
      'This drawing is too large or too complex to clean up here. Save it in CAD with fewer blocks, or split it.',
    check_not_run: 'The drawing check could not run. Try again.',
    round_timeout: 'The round took too long and was stopped. Try again.',
    unreadable_file: 'The drawing could not be read. Save it again in CAD and start a new clean-up.',
    stopped: 'The round was stopped.',
  },
  uploadRejected: {
    too_large: 'The file is larger than the council allows.',
    empty: 'The file is empty.',
    unsupported_type: 'The file is not a DWG or DXF drawing of the kind its name says.',
  },
  answerInvalid: {
    needs_item: 'This answer needs a fix you unticked or a question you did not accept.',
    conflicts_item: 'This answer conflicts with a fix that is ticked.',
    nothing_accepted: 'Accept at least one fix or answer before you apply.',
  },
  changes,
} as const
